import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuth, isStaffBuild } from '@/lib/auth/auth-context';
import { FullPageLoader } from '@/components/ui';

// Lazy load pages - Auth
const LoginPage = lazy(() => import('@/pages/auth/login/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const DeviceVerificationPage = lazy(() => import('@/pages/auth/device-verification/DeviceVerificationPage'));
const Setup2FAPage = lazy(() => import('@/pages/auth/setup-2fa/Setup2FAPage'));
const Verify2FAPage = lazy(() => import('@/pages/auth/verify-2fa/Verify2FAPage'));

// Lazy load pages - Setup
const SetupPage = lazy(() => import('@/pages/setup/index/SetupPage'));
const ConfirmationPage = lazy(() => import('@/pages/setup/confirmation/ConfirmationPage'));
const EmailConfirmationPage = lazy(() => import('@/pages/setup/confirm-email/EmailConfirmationPage'));

// Lazy load pages - Customer
const DashboardPage = lazy(() => import('@/pages/customer/dashboard/DashboardPage'));
const ServicesListPage = lazy(() => import('@/pages/customer/services/list/ServicesListPage'));
const ServiceDetailPage = lazy(() => import('@/pages/customer/services/detail/ServiceDetailPage'));
const WordPressManagePage = lazy(() => import('@/pages/customer/services/manage/wordpress/WordPressManagePage'));
const HostingManagePage = lazy(() => import('@/pages/customer/services/manage/hosting/HostingManagePage'));
const ProfileInfoPage = lazy(() => import('@/pages/customer/profile/info/ProfileInfoPage'));
const BillingPage = lazy(() => import('@/pages/customer/billing/BillingPage'));
const InvoiceDetailPage = lazy(() => import('@/pages/customer/billing/InvoiceDetailPage'));
const SupportPage = lazy(() => import('@/pages/customer/support/SupportPage'));
const TicketDetailPage = lazy(() => import('@/pages/customer/support/TicketDetailPage'));
const LiveChatPage = lazy(() => import('@/pages/customer/support/chat/LiveChatPage'));
const SurveyPage = lazy(() => import('@/pages/customer/support/survey/SurveyPage'));
const OrderPage = lazy(() => import('@/pages/customer/order/OrderPage'));
const OrderConfirmationPage = lazy(() => import('@/pages/customer/order/OrderConfirmationPage'));

// Eagerly load error pages (small, needed immediately)
import NotFoundPage from '@/pages/errors/not-found/NotFoundPage';
import RouteErrorPage from '@/pages/errors/route-error/RouteErrorPage';

// Staff-only pages
// @staff-only-start
const AdminDashboardPage = lazy(() => import('@/pages/staff/dashboard/AdminDashboardPage'));
const UsersListPage = lazy(() => import('@/pages/staff/users/list/UsersListPage'));
const UserDetailPage = lazy(() => import('@/pages/staff/users/detail/UserDetailPage'));
const GroupsListPage = lazy(() => import('@/pages/staff/groups/list/GroupsListPage'));
const GroupDetailPage = lazy(() => import('@/pages/staff/groups/detail/GroupDetailPage'));
const ProductsListPage = lazy(() => import('@/pages/staff/products/list/ProductsListPage'));
const ProductDetailPage = lazy(() => import('@/pages/staff/products/detail/ProductDetailPage'));
const AdminInvoicesListPage = lazy(() => import('@/pages/staff/billing/list/InvoicesListPage'));
const AdminInvoiceDetailPage = lazy(() => import('@/pages/staff/billing/detail/InvoiceDetailPage'));
const AdminPaymentsListPage = lazy(() => import('@/pages/staff/billing/list/PaymentsListPage'));
const SupportConsolePage = lazy(() => import('@/pages/staff/support-console/SupportConsolePage'));
const AdminTicketsListPage = lazy(() => import('@/pages/staff/tickets/list/TicketsListPage'));
const AdminTicketDetailPage = lazy(() => import('@/pages/staff/tickets/detail/TicketDetailPage'));
const SlaPoliciesPage = lazy(() => import('@/pages/staff/sla/SlaPoliciesPage'));
const SurveysPage = lazy(() => import('@/pages/staff/surveys/SurveysPage'));
const AdminLiveChatPage = lazy(() => import('@/pages/staff/live-chat/LiveChatPage'));
const AdminServicesListPage = lazy(() => import('@/pages/staff/services/ServicesListPage'));
const AddServicePage = lazy(() => import('@/pages/staff/services/AddServicePage'));
const AdminServiceDetailPage = lazy(() => import('@/pages/staff/services/ServiceDetailPage'));
const ServersOverviewPage = lazy(() => import('@/pages/staff/servers/ServersOverviewPage'));
const ServerDetailPage = lazy(() => import('@/pages/staff/servers/ServerDetailPage'));
const ServerSettingsPage = lazy(() => import('@/pages/staff/servers/ServerSettingsPage'));
const VpsManagePage = lazy(() => import('@/pages/customer/services/manage/vps/VpsManagePage'));
const IpAddressesPage = lazy(() => import('@/pages/staff/ips/IpAddressesPage'));
// @staff-only-end

function RequireAuth() {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Outlet />
    </Suspense>
  );
}

function RequireGuest() {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return <FullPageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Outlet />
    </Suspense>
  );
}

function RequireStaff() {
  const { isAuthenticated, isStaff, isInitializing } = useAuth();

  if (isInitializing) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isStaff) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Outlet />
    </Suspense>
  );
}

// Partial auth - user is logged in but needs additional verification
function RequirePartialAuth() {
  const { isAuthenticated, isInitializing } = useAuth();

  if (isInitializing) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Outlet />
    </Suspense>
  );
}

// Public routes - no auth check, accessible by anyone
function PublicRoute() {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Outlet />
    </Suspense>
  );
}

export function createAppRouter() {
  const routes = [
    // Public routes (no auth check - for flows that handle their own auth)
    {
      element: <PublicRoute />,
      errorElement: <RouteErrorPage />,
      children: [
        { path: '/wordpress-signup/verify/:token', element: <EmailConfirmationPage /> },
        { path: '/support/survey/:token', element: <SurveyPage /> },
      ],
    },

    // Guest routes (unauthenticated only)
    {
      element: <RequireGuest />,
      errorElement: <RouteErrorPage />,
      children: [
        { path: '/login', element: <LoginPage /> },
        { path: '/forgot-password', element: <ForgotPasswordPage /> },
        { path: '/reset-password', element: <ResetPasswordPage /> },
        { path: '/setup', element: <SetupPage /> },
        { path: '/wordpress-signup', element: <SetupPage /> },
        { path: '/setup/confirmation', element: <ConfirmationPage /> },
      ],
    },

    // Partial auth routes (logged in, but needs verification)
    {
      element: <RequirePartialAuth />,
      errorElement: <RouteErrorPage />,
      children: [
        { path: '/verify-device', element: <DeviceVerificationPage /> },
        { path: '/setup-2fa', element: <Setup2FAPage /> },
        { path: '/verify-2fa', element: <Verify2FAPage /> },
      ],
    },

    // Authenticated customer routes
    {
      element: <RequireAuth />,
      errorElement: <RouteErrorPage />,
      children: [
        { path: '/', element: <DashboardPage /> },
        { path: '/services', element: <ServicesListPage /> },
        { path: '/services/:id', element: <ServiceDetailPage /> },
        { path: '/services/:id/manage/wordpress', element: <WordPressManagePage /> },
        { path: '/services/:id/manage/hosting', element: <HostingManagePage /> },
        { path: '/services/:id/manage/vps', element: <VpsManagePage /> },
        { path: '/profile', element: <ProfileInfoPage /> },
        { path: '/profile/addresses', element: <ProfileInfoPage /> },
        { path: '/profile/payment-methods', element: <ProfileInfoPage /> },
        { path: '/profile/security', element: <ProfileInfoPage /> },
        { path: '/profile/preferences', element: <ProfileInfoPage /> },
        { path: '/billing', element: <BillingPage /> },
        { path: '/billing/invoices/:id', element: <InvoiceDetailPage /> },
        { path: '/support', element: <SupportPage /> },
        { path: '/support/chat', element: <LiveChatPage /> },
        { path: '/support/chat/:ticketId', element: <LiveChatPage /> },
        { path: '/support/:ticketId', element: <TicketDetailPage /> },
        { path: '/order', element: <OrderPage /> },
        { path: '/order/confirmation', element: <OrderConfirmationPage /> },
      ],
    },

    // 404 - eagerly loaded for instant navigation
    {
      path: '*',
      element: <NotFoundPage />,
      errorElement: <RouteErrorPage />,
    },
  ];

  // Add staff routes only in staff build
  if (isStaffBuild()) {
    routes.splice(3, 0, {
      element: <RequireStaff />,
      errorElement: <RouteErrorPage />,
      children: [
        { path: '/admin', element: <AdminDashboardPage /> },
        { path: '/admin/users', element: <UsersListPage /> },
        { path: '/admin/users/:id', element: <UserDetailPage /> },
        { path: '/admin/groups', element: <GroupsListPage /> },
        { path: '/admin/groups/:id', element: <GroupDetailPage /> },
        { path: '/admin/products', element: <ProductsListPage /> },
        { path: '/admin/products/create', element: <ProductDetailPage /> },
        { path: '/admin/products/:id', element: <ProductDetailPage /> },
        { path: '/admin/billing/invoices', element: <AdminInvoicesListPage /> },
        { path: '/admin/billing/invoices/:id', element: <AdminInvoiceDetailPage /> },
        { path: '/admin/billing/payments', element: <AdminPaymentsListPage /> },
        { path: '/admin/support-console', element: <SupportConsolePage /> },
        { path: '/admin/tickets', element: <AdminTicketsListPage /> },
        { path: '/admin/tickets/:ticketId', element: <AdminTicketDetailPage /> },
        { path: '/admin/sla-policies', element: <SlaPoliciesPage /> },
        { path: '/admin/surveys', element: <SurveysPage /> },
        { path: '/admin/live-chat', element: <AdminLiveChatPage /> },
        { path: '/admin/services', element: <AdminServicesListPage /> },
        { path: '/admin/services/create', element: <AddServicePage /> },
        { path: '/admin/services/:id', element: <AdminServiceDetailPage /> },
        { path: '/admin/servers', element: <ServersOverviewPage /> },
        { path: '/admin/servers/settings', element: <ServerSettingsPage /> },
        { path: '/admin/servers/:id', element: <ServerDetailPage /> },
        { path: '/admin/ip-addresses', element: <IpAddressesPage /> },
      ],
    });
  }

  return createBrowserRouter(routes);
}
